/**
 * Where a notification, a publication link or a tapped anchor may take the
 * user. This is a SECURITY guard, not a convenience: useSafeNavigate's watchdog
 * falls back to `window.location.assign(to)`, so an unvalidated destination is
 * an open redirect, and on native an unvalidated URL can put a Lemon Squeezy
 * checkout in the browser sheet (App Store rule 3.1.1).
 *
 * Import-free on purpose. The platform is passed in rather than read from
 * lib/platform, so this file stays a pure function of its arguments and its
 * test can run under react-scripts' jest (which cannot collect axios).
 */

export type ResolvedNotificationTarget =
  | { kind: "internal"; path: string }
  | { kind: "external"; url: string }
  | { kind: "none" };

/** What `POST /notifications/{id}/read` resolves, at the moment of opening. */
export type ServerNotificationTarget =
  | { kind: "internal"; path: string }
  | { kind: "external"; url: string }
  | { kind: "inbox" };

interface ResolveOptions {
  isNative: boolean;
}

export const INBOX_PATH = "/inbox";

/**
 * Our own web origins. An absolute URL on one of these is an internal route
 * wearing an external URL, and is rewritten to its path BEFORE any policy
 * runs — otherwise it takes the external branch and, on native (whose own
 * origin is capacitor://localhost), opens the web app in the browser sheet.
 */
export const FIRST_PARTY_ORIGINS: readonly string[] = [
  "https://app.insightanalytics.io",
  "https://staging.insightanalytics.io",
];

/**
 * First path segments a destination may start with. Asserted against the
 * router's route table in notificationTarget.test.ts, so a route cannot be
 * added to one and not the other.
 */
export const NOTIFICATION_ROUTE_SEGMENTS: readonly string[] = [
  "charts",
  "super-investors",
  "screener",
  "publications",
  "earnings",
  "inbox",
  "dashboard",
  "chat",
];

/** The email / Universal Link route. Accepted only as exactly /n/<uuid>. */
export const NOTIFICATION_LINK_SEGMENT = "n";

const NONE: ResolvedNotificationTarget = { kind: "none" };
const DUMMY_ORIGIN = "https://internal.invalid";
const INTERNAL_PATH_PATTERN = /^\/(?![/\\])/;
const NOTIFICATION_LINK_PATTERN =
  /^\/n\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NOTIFICATION_LINK_SEARCHES = ["", "?s=email", "?s=push"];

// Native must never reach a web billing surface. None of these is
// allow-listed today, so this is a second lock, not the only one: it still
// holds if a later change allow-lists `auth` or `membership`.
const NATIVE_BLOCKED_PATHS = ["/start-free-trial", "/membership", "/auth/settings"];
const BILLING_HOST = "lemonsqueezy.com";

const parseAbsoluteUrl = (rawUrl: string): URL | null => {
  try {
    return new URL(rawUrl);
  } catch {
    return null;
  }
};

const normaliseHost = (hostname: string): string =>
  hostname.toLowerCase().replace(/\.$/, "");

const firstPartyHosts = FIRST_PARTY_ORIGINS.map((origin) => new URL(origin).hostname);

const isFirstParty = (parsed: URL): boolean =>
  (parsed.protocol === "https:" || parsed.protocol === "http:") &&
  firstPartyHosts.includes(normaliseHost(parsed.hostname));

const isBillingHost = (parsed: URL): boolean => {
  const host = normaliseHost(parsed.hostname);
  return host === BILLING_HOST || host.endsWith(`.${BILLING_HOST}`);
};

/** An absolute http(s) URL on one of our own web origins. */
export const isFirstPartyUrl = (rawUrl: string): boolean => {
  const parsed = parseAbsoluteUrl(rawUrl);
  return parsed !== null && isFirstParty(parsed);
};

/** A Lemon Squeezy host (or any subdomain) — never opened from native. */
export const isBillingUrl = (rawUrl: string): boolean => {
  const parsed = parseAbsoluteUrl(rawUrl);
  return parsed !== null && isBillingHost(parsed);
};

// The router matches case-insensitively and against the decoded path, so the
// block has to as well.
const isNativeBlockedPath = (pathname: string): boolean => {
  let decoded = pathname;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    // Malformed escape — compare the raw path
  }
  const lowered = decoded.toLowerCase();
  return NATIVE_BLOCKED_PATHS.some(
    (blocked) => lowered === blocked || lowered.startsWith(`${blocked}/`),
  );
};

const resolveInternalPath = (
  rawPath: string,
  { isNative }: ResolveOptions,
): ResolvedNotificationTarget => {
  if (!INTERNAL_PATH_PATTERN.test(rawPath)) return NONE;

  let parsed: URL;
  try {
    parsed = new URL(rawPath, DUMMY_ORIGIN);
  } catch {
    return NONE;
  }
  // "/\t/evil.com" and friends: the parser strips the control character and
  // the path becomes protocol-relative. Anything that moved origin is out.
  if (parsed.origin !== DUMMY_ORIGIN) return NONE;

  // Everything below runs on the PARSED path, so "/charts/../auth/settings"
  // is judged as the "/auth/settings" it resolves to.
  const { pathname, search, hash } = parsed;
  if (isNative && isNativeBlockedPath(pathname)) return NONE;

  const segment = pathname.split("/")[1] ?? "";
  if (segment === NOTIFICATION_LINK_SEGMENT) {
    const isExactLink =
      NOTIFICATION_LINK_PATTERN.test(pathname) &&
      NOTIFICATION_LINK_SEARCHES.includes(search) &&
      hash === "";
    return isExactLink ? { kind: "internal", path: `${pathname}${search}` } : NONE;
  }
  if (!NOTIFICATION_ROUTE_SEGMENTS.includes(segment)) return NONE;

  return { kind: "internal", path: `${pathname}${search}${hash}` };
};

const resolveAbsoluteUrl = (
  rawUrl: string,
  options: ResolveOptions,
): ResolvedNotificationTarget => {
  const parsed = parseAbsoluteUrl(rawUrl);
  if (!parsed) return NONE;

  // First-party BEFORE policy: from here it is an internal path and can never
  // come back as external, whatever the internal rules decide.
  if (isFirstParty(parsed)) {
    return resolveInternalPath(`${parsed.pathname}${parsed.search}${parsed.hash}`, options);
  }

  if (parsed.protocol !== "https:") return NONE;
  if (options.isNative && isBillingHost(parsed)) return NONE;
  return { kind: "external", url: parsed.href };
};

/**
 * `path` decides alone when present (the server prefers it the same way);
 * `url` is only consulted for a notification that has no path. `none` means
 * "do not open this" — notification callers then fall back to the inbox.
 */
export const resolveNotificationTarget = (
  path: string | null | undefined,
  url: string | null | undefined,
  options: ResolveOptions,
): ResolvedNotificationTarget => {
  if (path) return resolveInternalPath(path, options);
  if (url) return resolveAbsoluteUrl(url, options);
  return NONE;
};

/** One authored href (a Markdown link, a tapped anchor): root-relative or absolute. */
export const resolveLinkTarget = (
  href: string,
  options: ResolveOptions,
): ResolvedNotificationTarget => {
  if (href.startsWith("/")) return resolveNotificationTarget(href, null, options);
  return resolveNotificationTarget(null, href, options);
};

/**
 * The server's verdict, re-checked by this binary's own guard. A `kind` this
 * binary has never heard of is treated as the inbox, where the row is visible.
 */
export const resolveServerTarget = (
  target: ServerNotificationTarget,
  options: ResolveOptions,
): ResolvedNotificationTarget => {
  if (target.kind === "internal") return resolveNotificationTarget(target.path, null, options);
  if (target.kind === "external") return resolveNotificationTarget(null, target.url, options);
  return { kind: "internal", path: INBOX_PATH };
};
